import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaWhatsapp, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { buildWhatsAppUrl, LUMIGO_DISPLAY_PHONE } from "../../utils/whatsapp";

const DEFAULT_SLIDES = [
    {
        id: "slide-1",
        title: "Bring Your Brand to Life",
        subtitle: "High-intensity industrial logo projectors engineered for warehouses, manufacturing plants, and heavy commercial service.",
        tag: "INDUSTRIAL LOGO PROJECTION",
        image: "/assets/lumigo/projector-1.jpg",
        ctaPrimaryLabel: "Explore Products",
        ctaPrimaryLink: "/products",
        ctaSecondaryLabel: "Request a Quote",
        ctaSecondaryLink: "/contact",
        order: 1,
        isActive: true,
    },
    {
        id: "slide-2",
        title: "Architectural & Hospitality Branding",
        subtitle: "Transform hotel facades, retail storefronts, and commercial entrances with weatherproof high-lumen optical projection.",
        tag: "ARCHITECTURAL & FACADE ILLUMINATION",
        image: "/assets/lumigo/projector-4.jpg",
        ctaPrimaryLabel: "View Applications",
        ctaPrimaryLink: "/applications",
        ctaSecondaryLabel: "Request a Quote",
        ctaSecondaryLink: "/contact",
        order: 2,
        isActive: true,
    },
    {
        id: "slide-3",
        title: "Custom Gobos & Decorative Projection",
        subtitle: "Vibrant monochrome and full-colour dichroic glass gobos crafted to exact vector artwork, logos, and festive monograms.",
        tag: "CUSTOM GOBOS & EVENTS",
        image: "/assets/lumigo/projector-14.jpg",
        ctaPrimaryLabel: "Explore Gobos",
        ctaPrimaryLink: "/products",
        ctaSecondaryLabel: "Custom Gobo Request",
        ctaSecondaryLink: "/contact",
        order: 3,
        isActive: true,
    },
    {
        id: "slide-4",
        title: "Safety & Commercial Signage",
        subtitle: "Replace worn-out painted floor lines and physical tape with vivid LED projection markers that never peel, fade, or wear out.",
        tag: "SAFETY & WAREHOUSE MARKINGS",
        image: "/assets/lumigo/projector-16.jpg",
        ctaPrimaryLabel: "Explore Hardware",
        ctaPrimaryLink: "/products",
        ctaSecondaryLabel: "Technical Consultation",
        ctaSecondaryLink: "/contact",
        order: 4,
        isActive: true,
    },
];

const HeroCarousel = ({ slides = [], carouselConfig = {}, onOpenEnquiry }) => {
    const navigate = useNavigate();
    const activeSlides = (slides && slides.length > 0 ? slides : DEFAULT_SLIDES).filter(s => s.isActive !== false);
    const intervalMs = carouselConfig.interval || 2000;
    const isEnabled = carouselConfig.enabled !== false;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const timerRef = useRef(null);

    // Preload all slide images to prevent flickering on transitions
    useEffect(() => {
        activeSlides.forEach(slide => {
            if (slide.image) {
                const img = new Image();
                img.src = slide.image;
            }
        });
    }, [activeSlides]);

    // Automatic slide rotation every 2 seconds (configurable)
    useEffect(() => {
        if (!isEnabled || isPaused || activeSlides.length <= 1) return;

        timerRef.current = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % activeSlides.length);
        }, intervalMs);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isEnabled, isPaused, activeSlides.length, intervalMs]);

    const handlePrev = (e) => {
        e?.stopPropagation();
        setCurrentIndex(prev => (prev - 1 + activeSlides.length) % activeSlides.length);
    };

    const handleNext = (e) => {
        e?.stopPropagation();
        setCurrentIndex(prev => (prev + 1) % activeSlides.length);
    };

    if (activeSlides.length === 0) return null;

    const currentSlide = activeSlides[currentIndex] || activeSlides[0];

    return (
        <section
            className="relative min-h-[580px] lg:h-[640px] bg-[#141B22] overflow-hidden flex items-center select-none pt-14"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            aria-roledescription="carousel"
            aria-label="LUMIGO Featured Gobo Projectors"
        >
            {/* Background Slides with Crossfade */}
            {activeSlides.map((slide, idx) => (
                <div
                    key={slide.id || idx}
                    className={`absolute inset-0 bg-cover bg-center transition-opacity duration-700 ease-in-out ${
                        idx === currentIndex ? "opacity-100 z-1" : "opacity-0 z-0 pointer-events-none"
                    }`}
                    style={{
                        backgroundImage: `url('${slide.image}')`,
                        backgroundPosition: "center 40%",
                    }}
                />
            ))}

            {/* Premium Gradient Overlay for perfect typography contrast */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1722]/95 via-[#0F1722]/80 to-black/30 z-2" />

            {/* Content Container (Consistent positioning across all slides) */}
            <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 w-full">
                <div key={currentSlide.id || currentIndex} className="max-w-2xl transition-all duration-500">
                    {/* Tag / Category Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#E3B629]/15 border border-[#E3B629]/30 text-[#E3B629] text-[11px] font-black uppercase tracking-[0.25em] mb-4">
                        <span className="w-2 h-2 rounded-full bg-[#E3B629] animate-pulse" />
                        {currentSlide.tag || "COMMERCIAL GOBO PROJECTORS"}
                    </div>

                    {/* Main Headline */}
                    <h1 className="text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight sm:leading-[1.08] tracking-tight mb-3 sm:mb-4 transition-all duration-500">
                        {currentSlide.title}
                    </h1>

                    {/* Subtext */}
                    <p className="text-gray-300 text-xs sm:text-sm lg:text-base leading-relaxed mb-6 sm:mb-8 max-w-xl font-normal transition-all duration-500">
                        {currentSlide.subtitle}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-3.5">
                        <button
                            onClick={() => {
                                if (currentSlide.ctaPrimaryLink) {
                                    navigate(currentSlide.ctaPrimaryLink);
                                } else {
                                    navigate("/products");
                                }
                            }}
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold text-xs sm:text-sm px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg flex items-center justify-center gap-2 transition shadow-md hover:shadow-lg uppercase tracking-wider text-center"
                        >
                            {currentSlide.ctaPrimaryLabel || "Explore Products"} <FaArrowRight className="text-xs" />
                        </button>

                        <button
                            onClick={() => {
                                if (onOpenEnquiry) {
                                    onOpenEnquiry();
                                } else {
                                    navigate("/contact");
                                }
                            }}
                            className="bg-white/10 hover:bg-white/20 text-white border border-white/25 font-bold text-xs sm:text-sm px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg transition backdrop-blur-sm uppercase tracking-wider text-center"
                        >
                            {currentSlide.ctaSecondaryLabel || "Request a Quote"}
                        </button>

                        <a
                            href={buildWhatsAppUrl(`Hello LUMIGO,\n\nI am interested in ${currentSlide.title} (${currentSlide.tag}). Please share technical hardware specs and commercial quotation.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#168B53] hover:bg-[#137446] text-white font-bold text-xs sm:text-sm px-4 py-3 sm:py-3.5 rounded-lg flex items-center justify-center gap-2 transition shadow-sm text-center"
                            title={`Chat on WhatsApp (${LUMIGO_DISPLAY_PHONE})`}
                        >
                            <FaWhatsapp className="text-base" />
                            <span>WhatsApp</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Manual Controls: Prev & Next Arrows (Visible on sm/md and larger screens) */}
            {activeSlides.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-[#E3B629] text-white hover:text-[#263746] border border-white/20 hover:border-[#E3B629] items-center justify-center transition shadow-lg backdrop-blur-xs"
                        aria-label="Previous Slide"
                    >
                        <FaChevronLeft className="text-sm -ml-0.5" />
                    </button>
                    <button
                        onClick={handleNext}
                        className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-[#E3B629] text-white hover:text-[#263746] border border-white/20 hover:border-[#E3B629] items-center justify-center transition shadow-lg backdrop-blur-xs"
                        aria-label="Next Slide"
                    >
                        <FaChevronRight className="text-sm -mr-0.5" />
                    </button>
                </>
            )}

            {/* Slide Indicator Dots */}
            {activeSlides.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10">
                    {activeSlides.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                idx === currentIndex
                                    ? "w-7 bg-[#E3B629]"
                                    : "w-2 bg-white/40 hover:bg-white/70"
                            }`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

export default HeroCarousel;
